import React, { useState, useContext } from 'react';
import axiosInstance from '../../utils/axiosInstance';
import { API_URL } from '../../utils/runtimeConfig';
import './PlanningMode.css';
import { ErrorContext } from '../../context/ErrorContext';
import Permission from '../../utils/HasPermission';
import CreatePlanModal from '../CreatePlanModal'; // Import the new modal

const PlanningMode = ({
  selectedNation,
  selectedPlan,
  setPlans,
  plans,
  setSelectedPlan,
  readOnly = false,
  canEdit,
}) => {
  const { showError } = useContext(ErrorContext);
  const hasWritePlansPermission = Permission('writePlans');
  const canEditPlans = typeof canEdit === 'boolean'
    ? canEdit
    : (hasWritePlansPermission && !readOnly);

  const [isModalOpen, setIsModalOpen] = useState(false); // State to control modal visibility
  const [isCreatingPlan, setIsCreatingPlan] = useState(false);
  const [isDuplicatingPlan, setIsDuplicatingPlan] = useState(false);

  const fetchPlans = async () => {
    try {
      const response = await axiosInstance.get(`${API_URL}/nations/${selectedNation._id}/plans`);
      setPlans(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching plans:', error);
      showError(error.message || 'Failed to fetch plans');
      return null;
    }
  };

  const handleCreatePlan = async (planName, mergePlan) => {
    const trimmedPlanName = String(planName || '').trim();
    if (!trimmedPlanName) {
      showError('Plan name is required.');
      return false;
    }

    setIsCreatingPlan(true);
    try {
      const response = await axiosInstance.post(
        `${API_URL}/nations/${selectedNation._id}/plans`,
        { name: trimmedPlanName, mergePlan }
      );
      const newPlan = response.data;
      setPlans((prevPlans) => [...prevPlans, newPlan]);
      setSelectedPlan(newPlan);
      setIsModalOpen(false);
      return true;
    } catch (error) {
      console.error('Error creating plan:', error);
      showError(error.message || 'Failed to create plan');
      return false;
    } finally {
      setIsCreatingPlan(false);
    }
  };

  const handlePlanChange = (e) => {
    const selectedPlanId = e.target.value;
    const selectedPlanObject = plans.find(plan => plan._id === selectedPlanId);
    setSelectedPlan(selectedPlanObject);
  };

  const handleDuplicatePlan = async () => {
    if (!selectedNation?._id || !selectedPlan?._id) {
      showError('Please select a plan to duplicate.');
      return;
    }

    const suggestedName = `${selectedPlan.name || 'Plan'} Copy`;
    const inputName = window.prompt('Name for duplicated plan:', suggestedName);
    if (inputName === null) {
      return;
    }
    const trimmedPlanName = String(inputName || '').trim();
    if (!trimmedPlanName) {
      showError('Plan name is required.');
      return;
    }

    const copyBorders = window.confirm('Copy alliance borders into duplicated plan? (OK = Yes, Cancel = No)');

    setIsDuplicatingPlan(true);
    try {
      const response = await axiosInstance.post(
        `${API_URL}/nations/${selectedNation._id}/plans/${selectedPlan._id}/duplicate`,
        { name: trimmedPlanName, copyBorders }
      );
      const newPlan = response.data;
      setPlans((prevPlans) => [...prevPlans, newPlan]);
      setSelectedPlan(newPlan);
    } catch (error) {
      console.error('Error duplicating plan:', error);
      showError(error?.response?.data?.message || error.message || 'Failed to duplicate plan');
    } finally {
      setIsDuplicatingPlan(false);
    }
  };

  const handleDeletePlan = async () => {
    try {
      if (selectedPlan?.name !== 'current') {
        const confirmation = window.confirm('Are you sure you want to delete this plan? This will delete all tiles for this plan and cannot be undone.');
        if (confirmation) {
          await axiosInstance.delete(`${API_URL}/nations/${selectedNation._id}/plans/${selectedPlan._id}`);
          fetchPlans(); // Refresh plans after deletion
          const currentPlan = plans.find(plan => plan.name === 'current');
          setSelectedPlan(currentPlan);
        }
      } else {
        showError('You cannot delete the current state.');
      }
    } catch (error) {
      console.error('Error deleting plan:', error);
      showError(error.message || 'Failed to delete plan');
    }
  };

  const handleOverwriteCurrentPlan = async () => {
    try {
      if (!selectedPlan || selectedPlan.name === 'current') {
        return;
      }

      const confirmation = window.confirm(
        'Overwrite the current plan with this plan? This is permanent and will delete the existing current plan and its tiles.'
      );
      if (!confirmation) return;

      await axiosInstance.post(
        `${API_URL}/nations/${selectedNation._id}/plans/${selectedPlan._id}/overwrite-current`
      );

      const updatedPlans = await fetchPlans();
      if (updatedPlans) {
        const currentPlan = updatedPlans.find(plan => plan.name === 'current');
        setSelectedPlan(currentPlan || null);
      }
    } catch (error) {
      console.error('Error overwriting current plan:', error);
      showError(error.message || 'Failed to overwrite current plan');
    }
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (isCreatingPlan) return;
    setIsModalOpen(false);
  };

  return (
    <div className="planning-mode">
      <label htmlFor="plan-selector">Plan: </label>
      <select
        id="plan-selector"
        className="plan-selector"
        value={selectedPlan?._id || ''}
        onChange={handlePlanChange}
      >
        {plans.map((plan) => (
          <option key={plan._id} value={plan._id}>
            {plan.name}
          </option>
        ))}
      </select>
      {canEditPlans && (
        <>
          <button onClick={openModal} className="create-plan-button" disabled={isCreatingPlan}>
            {isCreatingPlan ? 'Creating...' : 'New Plan'}
          </button>
          <button
            onClick={handleDuplicatePlan}
            className="duplicate-plan-button"
            disabled={isDuplicatingPlan || !selectedPlan}
          >
            {isDuplicatingPlan ? 'Duplicating...' : 'Duplicate Plan'}
          </button>
          <button
            onClick={handleOverwriteCurrentPlan}
            className="overwrite-plan-button"
            disabled={!selectedPlan || selectedPlan.name === 'current' || isDuplicatingPlan}
          >
            Overwrite Current
          </button>
          <button onClick={handleDeletePlan} className="delete-plan-button">Delete Plan</button>
        </>
      )}
      <CreatePlanModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSave={handleCreatePlan}
        isSaving={isCreatingPlan}
      />
    </div>
  );
};


export default PlanningMode;
